import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, Users, FileText, Settings, LogOut, ArrowLeft,
  Save, Loader2, CheckCircle, AlertCircle, Eye, EyeOff,
  Wifi, WifiOff, RefreshCw, Info
} from 'lucide-react';
import api from '../utils/api';

// Agent pipeline configuration constants
const PROVIDER_OPTIONS = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'xai', label: 'xAI (Grok)' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'bedrock', label: 'Amazon Bedrock' },
];

// AWS regions that offer Amazon Bedrock. Default is eu-west-2 (London) for
// UK/EU data residency. Adding a region here makes it selectable; the model
// list is always pulled live from whichever region is chosen.
const BEDROCK_REGIONS = [
  { value: 'eu-west-2',      label: 'eu-west-2 — London (default)' },
  { value: 'eu-west-1',      label: 'eu-west-1 — Ireland' },
  { value: 'eu-central-1',   label: 'eu-central-1 — Frankfurt' },
  { value: 'us-east-1',      label: 'us-east-1 — N. Virginia' },
  { value: 'us-west-2',      label: 'us-west-2 — Oregon' },
  { value: 'ap-southeast-1', label: 'ap-southeast-1 — Singapore' },
  { value: 'ap-northeast-1', label: 'ap-northeast-1 — Tokyo' },
];

const PROVIDER_MODELS = {
  anthropic: [
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 ($5/$25)' },
    { value: 'claude-sonnet-4-6-20260217', label: 'Claude Sonnet 4.6 ($3/$15)' },
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 ($5/$25)' },
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 ($3/$15)' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 ($1/$5)' },
  ],
  openai: [
    { value: 'gpt-5.4', label: 'GPT-5.4 ($2.50/$15)' },
    { value: 'gpt-5.4-pro', label: 'GPT-5.4 Pro ($30/$180)' },
    { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini ($0.75/$3)' },
    { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano ($0.15/$0.60)' },
    { value: 'gpt-5-2025-08-07', label: 'GPT-5' },
    { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'o3-pro-2025-06-10', label: 'o3 Pro' },
    { value: 'o4-mini', label: 'o4-mini ($1.10/$4.40)' },
    { value: 'o4-mini-deep-research-2025-06-26', label: 'o4-mini Deep Research' },
    { value: 'gpt-4o', label: 'GPT-4o (Legacy)' },
  ],
  google: [
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview' },
    { value: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
    { value: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro' },
  ],
  xai: [
    { value: 'grok-4', label: 'Grok 4 (non-ZDR)' },
    { value: 'grok-4.1-fast', label: 'Grok 4.1 Fast 2M ctx (non-ZDR)' },
  ],
  openrouter: [
    // ── ZDR models — alphabetical by model ID ────────────────────────────────
    // Amazon Nova (Bedrock, ZDR)
    { value: 'amazon/nova-lite-v1',      label: 'amazon/nova-lite-v1 ($0.06/$0.24) ZDR 300K ctx' },
    { value: 'amazon/nova-premier-v1',   label: 'amazon/nova-premier-v1 ($2.50/$12.50) ZDR 1M ctx' },
    { value: 'amazon/nova-pro-v1',       label: 'amazon/nova-pro-v1 ($0.80/$3.20) ZDR 300K ctx' },
    // Anthropic (Bedrock → Vertex, ZDR)
    { value: 'anthropic/claude-3.5-haiku',          label: 'anthropic/claude-3.5-haiku ($0.80/$4) ZDR 200K ctx' },
    { value: 'anthropic/claude-3.7-sonnet',         label: 'anthropic/claude-3.7-sonnet ($3/$15) ZDR 200K ctx' },
    { value: 'anthropic/claude-haiku-4.5',          label: 'anthropic/claude-haiku-4.5 ($1/$5) ZDR 200K ctx' },
    { value: 'anthropic/claude-opus-4.6',           label: 'anthropic/claude-opus-4.6 ($5/$25) ZDR 1M ctx' },
    { value: 'anthropic/claude-opus-4.7',           label: 'anthropic/claude-opus-4.7 ($15/$75) ZDR 1M ctx ★' },
    { value: 'anthropic/claude-sonnet-4',           label: 'anthropic/claude-sonnet-4 ($3/$15) ZDR 1M ctx' },
    { value: 'anthropic/claude-sonnet-4.5',         label: 'anthropic/claude-sonnet-4.5 ($3/$15) ZDR 1M ctx' },
    { value: 'anthropic/claude-sonnet-4.6',         label: 'anthropic/claude-sonnet-4.6 ($3/$15) ZDR [Bedrock→Vertex] 1M ctx' },
    { value: 'anthropic/claude-sonnet-4.6::bedrock',label: 'anthropic/claude-sonnet-4.6::bedrock ($3/$15) ZDR ★ 1M ctx' },
    { value: 'anthropic/claude-sonnet-4.6::vertex', label: 'anthropic/claude-sonnet-4.6::vertex ($3/$15) ZDR 1M ctx' },
    // DeepSeek (ZDR)
    { value: 'deepseek/deepseek-chat',          label: 'deepseek/deepseek-chat ($0.32/$0.89) ZDR 163K ctx' },
    { value: 'deepseek/deepseek-chat-v3-0324',  label: 'deepseek/deepseek-chat-v3-0324 ($0.20/$0.77) ZDR ★ budget 163K ctx' },
    { value: 'deepseek/deepseek-r1',            label: 'deepseek/deepseek-r1 ($0.70/$2.50) ZDR reasoning 64K ctx' },
    { value: 'deepseek/deepseek-r1-0528',       label: 'deepseek/deepseek-r1-0528 ($0.70/$2.15) ZDR reasoning 163K ctx' },
    { value: 'deepseek/deepseek-v3.1',          label: 'deepseek/deepseek-v3.1 ($0.15/$0.75) ZDR 32K ctx' },
    { value: 'deepseek/deepseek-v3.2',          label: 'deepseek/deepseek-v3.2 ($0.26/$0.38) ZDR 163K ctx' },
    // Google (Vertex, ZDR)
    { value: 'google/gemini-2.0-flash-001',              label: 'google/gemini-2.0-flash-001 ($0.10/$0.40) ZDR native-search 1M ctx' },
    { value: 'google/gemini-2.0-flash-lite-001',         label: 'google/gemini-2.0-flash-lite-001 ($0.075/$0.30) ZDR native-search 1M ctx ★' },
    { value: 'google/gemini-2.5-flash',                  label: 'google/gemini-2.5-flash ($0.30/$2.50) ZDR native-search 1M ctx ★' },
    { value: 'google/gemini-2.5-pro',                    label: 'google/gemini-2.5-pro ($1.25/$10) ZDR native-search [Vertex] 1M ctx' },
    { value: 'google/gemini-2.5-pro-preview',            label: 'google/gemini-2.5-pro-preview ($1.25/$10) ZDR native-search 1M ctx' },
    { value: 'google/gemini-2.5-pro-preview-05-06',      label: 'google/gemini-2.5-pro-preview-05-06 ($1.25/$10) ZDR native-search 1M ctx' },
    { value: 'google/gemini-3.1-flash-lite-preview',     label: 'google/gemini-3.1-flash-lite-preview ($0.25/$1.50) ZDR native-search 1M ctx ★' },
    { value: 'google/gemini-3.1-pro-preview',            label: 'google/gemini-3.1-pro-preview ($2/$12) ZDR 1M ctx' },
    // Meta Llama (ZDR)
    { value: 'meta-llama/llama-3.3-70b-instruct', label: 'meta-llama/llama-3.3-70b-instruct ($0.10/$0.32) ZDR 131K ctx' },
    { value: 'meta-llama/llama-4-maverick',       label: 'meta-llama/llama-4-maverick ($0.15/$0.60) ZDR 1M ctx' },
    { value: 'meta-llama/llama-4-scout',          label: 'meta-llama/llama-4-scout ($0.08/$0.30) ZDR 327K ctx' },
    // MiniMax (ZDR)
    { value: 'minimax/minimax-m1',   label: 'minimax/minimax-m1 ($0.44/$1.76) ZDR 1M ctx' },
    { value: 'minimax/minimax-m2',   label: 'minimax/minimax-m2 ($0.26/$1) ZDR 196K ctx' },
    { value: 'minimax/minimax-m2.7', label: 'minimax/minimax-m2.7 ($0.30/$1.20) ZDR 204K ctx' },
    // Mistral (ZDR)
    { value: 'mistralai/mistral-small-2603',            label: 'mistralai/mistral-small-2603 ($0.19/$0.75) ZDR 256K ctx' },
    { value: 'mistralai/mistral-small-3.2-24b-instruct',label: 'mistralai/mistral-small-3.2-24b-instruct ($0.07/$0.20) ZDR 128K ctx' },
    // Moonshot / Kimi (ZDR)
    { value: 'moonshotai/kimi-k2',         label: 'moonshotai/kimi-k2 ($0.57/$2.30) ZDR 131K ctx' },
    { value: 'moonshotai/kimi-k2-0905',    label: 'moonshotai/kimi-k2-0905 ($0.40/$2) ZDR 262K ctx' },
    { value: 'moonshotai/kimi-k2-thinking',label: 'moonshotai/kimi-k2-thinking ($0.60/$2.50) ZDR 262K ctx' },
    { value: 'moonshotai/kimi-k2.5',       label: 'moonshotai/kimi-k2.5 ($0.38/$1.72) ZDR [Exa] 262K ctx' },
    { value: 'moonshotai/kimi-k2.6',       label: 'moonshotai/kimi-k2.6 ZDR 262K ctx ★' },
    // NVIDIA (ZDR)
    { value: 'nvidia/llama-3.1-nemotron-ultra-253b-v1', label: 'nvidia/llama-3.1-nemotron-ultra-253b-v1 ($0.60/$1.80) ZDR 131K ctx' },
    // OpenAI (Azure, ZDR)
    { value: 'openai/gpt-4.1',       label: 'openai/gpt-4.1 ($2/$8) ZDR [Azure] 1M ctx' },
    { value: 'openai/gpt-4.1-mini',  label: 'openai/gpt-4.1-mini ($0.40/$1.60) ZDR [Azure] 1M ctx' },
    { value: 'openai/gpt-4o',        label: 'openai/gpt-4o ($2.50/$10) ZDR [Azure] 128K ctx' },
    { value: 'openai/gpt-5',         label: 'openai/gpt-5 ($1.25/$10) ZDR [Azure] 400K ctx' },
    { value: 'openai/gpt-5-mini',    label: 'openai/gpt-5-mini ($0.25/$2) ZDR [Azure] 400K ctx' },
    { value: 'openai/gpt-5.1',       label: 'openai/gpt-5.1 ($1.25/$10) ZDR [Azure] 400K ctx' },
    { value: 'openai/gpt-5.1-codex', label: 'openai/gpt-5.1-codex ($1.25/$10) ZDR [Azure] 400K ctx' },
    { value: 'openai/gpt-5.2',       label: 'openai/gpt-5.2 ($1.75/$14) ZDR [Azure] 400K ctx' },
    { value: 'openai/gpt-5.3-chat',  label: 'openai/gpt-5.3-chat ($1.75/$14) ZDR [Azure] 128K ctx' },
    { value: 'openai/gpt-5.4',       label: 'openai/gpt-5.4 ($2.50/$15) ZDR [Azure] 1M ctx' },
    { value: 'openai/gpt-5.4-mini',  label: 'openai/gpt-5.4-mini ($0.75/$4.50) ZDR [Azure+Bing] 400K ctx ★' },
    { value: 'openai/gpt-5.4-nano',  label: 'openai/gpt-5.4-nano ($0.20/$1.25) ZDR [Azure] 400K ctx' },
    // Qwen (ZDR)
    { value: 'qwen/qwen3-32b',                     label: 'qwen/qwen3-32b ($0.08/$0.28) ZDR 40K ctx' },
    { value: 'qwen/qwen3-235b-a22b-thinking-2507', label: 'qwen/qwen3-235b-a22b-thinking-2507 ($0.23/$2.30) ZDR 262K ctx' },
    { value: 'qwen/qwq-32b',                       label: 'qwen/qwq-32b ($0.15/$0.58) ZDR reasoning 131K ctx' },
    // Z-AI / GLM (ZDR)
    { value: 'z-ai/glm-5', label: 'z-ai/glm-5 ($0.95/$2.55) ZDR 204K ctx' },
    // ── Perplexity native search (at end — no OR web plugin added) ───────────
    { value: 'perplexity/sonar',              label: 'perplexity/sonar ($1/$1) native-search ★ 2a' },
    { value: 'perplexity/sonar-deep-research',label: 'perplexity/sonar-deep-research ($2/$8) native-search ★ 2b' },
    { value: 'perplexity/sonar-pro',          label: 'perplexity/sonar-pro ($3/$15) native-search' },
    { value: 'perplexity/sonar-reasoning-pro',label: 'perplexity/sonar-reasoning-pro ($2/$8) native-search reasoning' },
  ],
};

// Flattened list of all models across all providers (for fallback chain dropdowns)
const ALL_MODELS = Object.entries(PROVIDER_MODELS).flatMap(([provider, models]) => {
  const providerLabel = PROVIDER_OPTIONS.find(p => p.value === provider)?.label || provider;
  return models.map(m => ({ value: m.value, label: `${providerLabel} — ${m.label}`, provider }));
});

const AGENT_ROLES = [
  { key: 'webResearch', label: 'Web Research Agent (2a)', description: 'Broad web research — Perplexity Sonar native search (Perplexity + Bing index)', step: '2a' },
  { key: 'deepResearch', label: 'Deep Research Agent (2b)', description: 'Deep multi-hop OSINT — Perplexity Sonar Deep Research native search', step: '2b' },
  { key: 'advancedResearch', label: 'Advanced Research Agent (2c)', description: 'Analytical research via Claude Sonnet 4.6 OR Bedrock ZDR — web plugin (Brave index)', step: '2c' },
  { key: 'supplementaryResearch', label: 'Supplementary Research Agent (2c-sup)', description: 'Gap-filling research via GPT-5.4 Mini OR ZDR (Azure, Bing index)', step: '2c-sup' },
  { key: 'secondarySupplementaryResearch', label: 'Secondary Supplementary Research Agent (2d)', description: 'Additional colour via Kimi K2.5 OR ZDR — web plugin (Exa index)', step: '2d' },
  { key: 'arranger', label: 'Arranger Agent', description: 'Writes full formatted report from combined research', step: 3 },
  { key: 'checker', label: 'Checking Agent', description: 'Quality assessment and scoring', step: 4 },
  { key: 'orchestrator', label: 'Orchestrator Agent', description: 'Final review and pipeline management', step: 5 },
];

// API maximum output tokens per model (used as defaults and upper limits)
const API_MAX_TOKENS = {
  // Anthropic direct
  'claude-opus-4-6': 128000, 'claude-sonnet-4-6-20260217': 64000,
  'claude-opus-4-5-20251101': 64000, 'claude-sonnet-4-5-20250929': 64000,
  'claude-haiku-4-5-20251001': 64000,
  // OpenAI direct
  'gpt-5.4': 128000, 'gpt-5.4-pro': 128000, 'gpt-5.4-mini': 128000, 'gpt-5.4-nano': 128000,
  'gpt-5-2025-08-07': 128000, 'gpt-5-mini': 128000, 'gpt-4.1': 32768, 'gpt-4o': 16384,
  'o3-pro-2025-06-10': 100000, 'o4-mini': 100000, 'o4-mini-deep-research-2025-06-26': 100000,
  // Google direct
  'gemini-3.1-pro-preview': 65536, 'gemini-3.1-flash-lite': 32768,
  'gemini-2.5-pro-preview-05-06': 65536,
  // xAI direct
  'grok-4': 131072, 'grok-4.1-fast': 131072,
  // OpenRouter — Amazon Nova
  'amazon/nova-lite-v1': 5120, 'amazon/nova-pro-v1': 5120, 'amazon/nova-premier-v1': 32000,
  // OpenRouter — Anthropic
  'anthropic/claude-3.5-haiku': 8192, 'anthropic/claude-3.7-sonnet': 128000,
  'anthropic/claude-haiku-4.5': 64000, 'anthropic/claude-opus-4.6': 128000, 'anthropic/claude-opus-4.7': 128000,
  'anthropic/claude-sonnet-4': 64000, 'anthropic/claude-sonnet-4.5': 64000,
  'anthropic/claude-sonnet-4.6': 128000,
  'anthropic/claude-sonnet-4.6::bedrock': 128000, 'anthropic/claude-sonnet-4.6::vertex': 128000,
  // OpenRouter — DeepSeek
  'deepseek/deepseek-chat': 163840, 'deepseek/deepseek-chat-v3-0324': 131072,
  'deepseek/deepseek-r1': 16000, 'deepseek/deepseek-r1-0528': 131072,
  'deepseek/deepseek-v3.1': 7168, 'deepseek/deepseek-v3.2': 65536,
  // OpenRouter — Google
  'google/gemini-2.0-flash-001': 65536, 'google/gemini-2.0-flash-lite-001': 65536,
  'google/gemini-2.5-flash': 65535, 'google/gemini-2.5-pro': 65536,
  'google/gemini-2.5-pro-preview': 65536, 'google/gemini-2.5-pro-preview-05-06': 65535,
  'google/gemini-3.1-flash-lite-preview': 65536, 'google/gemini-3.1-pro-preview': 65536,
  // OpenRouter — Meta Llama
  'meta-llama/llama-3.3-70b-instruct': 16384,
  'meta-llama/llama-4-maverick': 16384, 'meta-llama/llama-4-scout': 16384,
  // OpenRouter — MiniMax
  'minimax/minimax-m1': 40000, 'minimax/minimax-m2': 131072, 'minimax/minimax-m2.7': 131072,
  // OpenRouter — Mistral
  'mistralai/mistral-small-2603': 65536, 'mistralai/mistral-small-3.2-24b-instruct': 65536,
  // OpenRouter — Moonshot / Kimi
  'moonshotai/kimi-k2': 131072, 'moonshotai/kimi-k2-0905': 262144,
  'moonshotai/kimi-k2-thinking': 131072, 'moonshotai/kimi-k2.5': 262144, 'moonshotai/kimi-k2.6': 262144,
  // OpenRouter — NVIDIA
  'nvidia/llama-3.1-nemotron-ultra-253b-v1': 131072,
  // OpenRouter — OpenAI
  'openai/gpt-4.1': 65536, 'openai/gpt-4.1-mini': 65536, 'openai/gpt-4o': 16384,
  'openai/gpt-5': 128000, 'openai/gpt-5-mini': 128000,
  'openai/gpt-5.1': 128000, 'openai/gpt-5.1-codex': 128000,
  'openai/gpt-5.2': 128000, 'openai/gpt-5.3-chat': 16384,
  'openai/gpt-5.4': 128000, 'openai/gpt-5.4-mini': 128000, 'openai/gpt-5.4-nano': 128000,
  // OpenRouter — Qwen
  'qwen/qwen3-32b': 40960, 'qwen/qwen3-235b-a22b-thinking-2507': 131072, 'qwen/qwq-32b': 131072,
  // OpenRouter — Z-AI
  'z-ai/glm-5': 131072,
  // OpenRouter — Perplexity (native search)
  'perplexity/sonar': 8000, 'perplexity/sonar-deep-research': 8000,
  'perplexity/sonar-pro': 8000, 'perplexity/sonar-reasoning-pro': 8000,
};

// Friendly labels + risk levels for the admin Feature Flags panel.
// Keys must match flag names defined in backend Config.getDefaultAISettings().features.
// risk: 'low' (no user impact), 'medium' (changes admin-visible behaviour), 'high' (touches user-visible surface).
const FEATURE_FLAG_DESCRIPTIONS = {
  solanaAuditEnabled: {
    label: 'Enable Solana on-chain audit hash writes',
    description: 'Computes SHA-256 of each completed report and writes it to the Solana Memo Program. Requires SOLANA_AUDIT_PRIVATE_KEY env var to be set; no-ops silently otherwise.',
    risk: 'medium',
  },
  solanaAuditVisible: {
    label: 'Show "Verified on Solana" badge on user reports',
    description: 'When ON, normal users see the audit badge on their reports. Keep OFF until a wallet is funded and on-chain writes have been verified.',
    risk: 'high',
  },
  verifyPagePublic: {
    label: 'Make /verify report-verifier page public',
    description: 'Mounts a public route at /verify so recipients can paste a report and check the on-chain hash. Keep OFF until UX is finalised.',
    risk: 'high',
  },
  adminAuditPanel: {
    label: 'Enable admin-only Audit panel at /admin-portal-7x9k2m/audit',
    description: 'Reuses the verifier UI under an admin-only authenticated route — safe for testing the Solana flow without exposing it to users.',
    risk: 'low',
  },
  solanaDevnetEnabled: {
    label: 'Use Solana devnet for audit writes (testing only)',
    description: 'Routes audit writes to devnet instead of mainnet-beta. Requires SOLANA_AUDIT_PRIVATE_KEY_DEVNET. Useful for end-to-end testing without spending real SOL.',
    risk: 'low',
  },
  agentApiEnabled: {
    label: 'Enable Agent-to-Agent API endpoints',
    description: 'Mounts /api/agent/* routes for AI-agent customers. Requires API key middleware, USDC top-ups, webhooks. Keep OFF until Tier 1 is fully built and tested.',
    risk: 'high',
  },
  adminApiKeysPanel: {
    label: 'Enable admin API Keys management panel',
    description: 'Adds an admin UI for creating / listing / revoking API keys. Safe to enable for admin testing — does not expose any user-facing endpoints.',
    risk: 'low',
  },
  oauthEnabled: {
    label: 'Enable OAuth 2.0 Authorization Server endpoints',
    description: 'Mounts /oauth/authorize, /oauth/token, /oauth/revoke, /oauth/introspect. Required for Salesforce AppExchange managed-package customers. Keep OFF until: (1) full OAuth handler implementations land (Phase 3), (2) consent UI exists in frontend, (3) admin Connected Apps panel is built, (4) penetration test has passed. Today, scaffolded handlers return 501.',
    risk: 'high',
  },
  enrichmentIE: {
    label: 'Enable Ireland (CRO) data enrichment',
    description: 'Adds CRO.ie company filings + Revenue.ie VAT to the research pipeline for Irish subjects. Free API.',
    risk: 'medium',
  },
  enrichmentFR: {
    label: 'Enable France (INPI/RNE/BODACC) enrichment',
    description: 'Adds French corporate registry, official announcements, and HATVP. Requires INPI API key. Free.',
    risk: 'medium',
  },
  enrichmentPT: {
    label: 'Enable Portugal (RCBE/Portal da Empresa) enrichment',
    description: 'Adds Portuguese beneficial-ownership and company registration data. Free.',
    risk: 'medium',
  },
  enrichmentGlobal: {
    label: 'Enable global free-data enrichment layer',
    description: 'Wires OpenSanctions, GLEIF, OpenCorporates (free tier), GDELT, OFAC SDN, UK OFSI, SEC EDGAR into the pipeline.',
    risk: 'medium',
  },
  perModelCostDisplay: {
    label: 'Show per-model rate columns + provider summary on reports',
    description: 'Adds input/output $/MTok columns and a "cost by provider" summary to ReportView and AdminReports. Display-only; no pricing changes.',
    risk: 'low',
  },
  helioCheckoutEnabled: {
    label: 'Enable Helio (Solana USDC) checkout on Pricing page',
    description: 'Adds a "Pay with crypto" button alongside Stripe. Requires Helio account + webhook. Keep OFF until live keys are provisioned.',
    risk: 'high',
  },
};

const AdminConfig = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    aiProvider: 'openai',
    openaiApiKey: '',
    anthropicApiKey: '',
    googleApiKey: '',
    xaiApiKey: '',
    openrouterApiKey: '',
    bedrockAccessKeyId: '',
    bedrockSecretAccessKey: '',
    bedrockRegion: 'eu-west-2',
    openaiModel: 'gpt-4o',
    anthropicModel: 'claude-sonnet-4-20250514',
    googleModel: 'gemini-pro',
    singleReportPrice: 9900,
    essentialMonthly: 19900,
    professionalMonthly: 49900,

    // NEW: Claude advanced settings
    claudeEffortLevel: 'high',      // low, medium, high, max
    claudeMaxWebSearches: 25        // numeric (5-100)
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingApiKeys, setSavingApiKeys] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showKeys, setShowKeys] = useState({});
  const [systemDefaultModel, setSystemDefaultModel] = useState('claude-opus-4-6');
  const [updatingModel, setUpdatingModel] = useState(false);

  // Agent pipeline state
  const [agentPipelineEnabled, setAgentPipelineEnabled] = useState(true);
  const [qualityThreshold, setQualityThreshold] = useState(7);
  const [maxRetries, setMaxRetries] = useState(2);
  const [minWordCount, setMinWordCount] = useState(8000);
  const [agentModels, setAgentModels] = useState({
    webResearch: { provider: 'google', model: 'gemini-2.5-pro-preview-05-06' },
    deepResearch: { provider: 'openai', model: 'o4-mini-deep-research-2025-06-26' },
    advancedResearch: { provider: 'anthropic', model: 'claude-opus-4-6' },
    arranger: { provider: 'anthropic', model: 'claude-opus-4-6' },
    checker: { provider: 'anthropic', model: 'claude-sonnet-4-5-20250929' },
    orchestrator: { provider: 'anthropic', model: 'claude-opus-4-6' },
  });
  const [savingAgentConfig, setSavingAgentConfig] = useState(false);
  const [fallbackModels, setFallbackModels] = useState(['', '', '']);
  const [savingFallback, setSavingFallback] = useState(false);

  // Backend models (loaded from Config.models for dynamic dropdowns)
  const [backendModels, setBackendModels] = useState(null);

  // API Provider Status state
  const [apiKeyStatus, setApiKeyStatus] = useState({ openai: false, anthropic: false, google: false, xai: false, openrouter: false });
  const [providerTestResults, setProviderTestResults] = useState({});
  const [testingProvider, setTestingProvider] = useState(null);

  // OpenRouter model registry refresh state
  const [refreshingModels, setRefreshingModels] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [showZdrList, setShowZdrList] = useState(false);
  const [updaterHistory, setUpdaterHistory] = useState([]);
  const [showUpdaterHistory, setShowUpdaterHistory] = useState(false);

  // Feature flags state — admin-only preview toggles for in-progress modules.
  // All flags default to false in backend (Config.getDefaultAISettings.features).
  // Toggling a flag here only changes admin-visible behaviour; nothing user-facing
  // should depend on a flag without being explicitly hidden behind it.
  const [features, setFeatures] = useState({});
  const [savingFeature, setSavingFeature] = useState(null);
  const [showFeatureFlags, setShowFeatureFlags] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchFeatures();
    loadUpdaterHistory();
  }, []);

  const fetchFeatures = async () => {
    try {
      const data = await api.admin.getFeatures();
      setFeatures(data || {});
    } catch (err) {
      console.error('Failed to fetch feature flags:', err.message);
    }
  };

  const handleToggleFeature = async (flagName) => {
    const newValue = !features[flagName];
    setSavingFeature(flagName);
    setMessage({ type: '', text: '' });
    try {
      const result = await api.admin.updateFeatures({ [flagName]: newValue });
      // Refresh the full set to reflect server-side state
      if (result?.features) {
        setFeatures(result.features);
      } else {
        setFeatures(prev => ({ ...prev, [flagName]: newValue }));
      }
      setMessage({ type: 'success', text: `Feature flag '${flagName}' set to ${newValue}` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update feature flag' });
    } finally {
      setSavingFeature(null);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await api.admin.getConfig();
      if (response.apiKeyStatus) {
        setApiKeyStatus(response.apiKeyStatus);
      }
      // Pre-populate provider test results if available from server
      if (response.storedTestResults) {
        setProviderTestResults(response.storedTestResults);
      }
      setConfig(prev => ({ ...prev, ...response }));

      // Fetch AI settings to get current default model and agent pipeline config
      const aiSettingsResponse = await api.admin.getAISettings();
      if (aiSettingsResponse?.defaultModel) {
        setSystemDefaultModel(aiSettingsResponse.defaultModel);
      }
      // Load agent pipeline settings
      if (aiSettingsResponse?.useAgentPipeline !== undefined) {
        setAgentPipelineEnabled(aiSettingsResponse.useAgentPipeline);
      }
      if (aiSettingsResponse?.qualityThreshold) {
        setQualityThreshold(aiSettingsResponse.qualityThreshold);
      }
      if (aiSettingsResponse?.maxRetries != null) {
        setMaxRetries(aiSettingsResponse.maxRetries);
      }
      if (aiSettingsResponse?.minWordCount != null) {
        setMinWordCount(aiSettingsResponse.minWordCount);
      }
      if (aiSettingsResponse?.agentModels) {
        setAgentModels(prev => ({ ...prev, ...aiSettingsResponse.agentModels }));
      }
      // Load backend models for dynamic dropdowns
      if (aiSettingsResponse?.models) {
        setBackendModels(aiSettingsResponse.models);
      }
      // Load Claude advanced settings from AI settings
      if (aiSettingsResponse?.claudeEffortLevel || aiSettingsResponse?.claudeMaxWebSearches) {
        setConfig(prev => ({
          ...prev,
          claudeEffortLevel: aiSettingsResponse.claudeEffortLevel || prev.claudeEffortLevel,
          claudeMaxWebSearches: aiSettingsResponse.claudeMaxWebSearches || prev.claudeMaxWebSearches
        }));
      }
      if (aiSettingsResponse?.fallbackModels) {
        // Pad to 3 entries
        const fb = [...(aiSettingsResponse.fallbackModels || [])];
        while (fb.length < 3) fb.push('');
        setFallbackModels(fb.slice(0, 3));
      }
    } catch (err) {
      console.error('Failed to fetch config:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await api.admin.updateConfig(config);
      setMessage({ type: 'success', text: 'Configuration saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApiKeys = async () => {
    setSavingApiKeys(true);
    setMessage({ type: '', text: '' });
    try {
      await api.admin.updateApiKeys({
        openai: config.openaiApiKey,
        anthropic: config.anthropicApiKey,
        google: config.googleApiKey,
        xai: config.xaiApiKey,
        openrouter: config.openrouterApiKey,
        bedrockAccessKeyId: config.bedrockAccessKeyId,
        bedrockSecretAccessKey: config.bedrockSecretAccessKey,
        bedrockRegion: config.bedrockRegion
      });
      setMessage({ type: 'success', text: 'API keys updated successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update API keys' });
    } finally {
      setSavingApiKeys(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  const toggleShowKey = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveFallbackChain = async () => {
    setSavingFallback(true);
    setMessage({ type: '', text: '' });
    try {
      await api.admin.updateFallbackChain({
        primaryFallback: systemDefaultModel,
        fallbackModels: fallbackModels.filter(m => m)
      });
      setMessage({ type: 'success', text: 'Fallback model chain saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save fallback chain' });
    } finally {
      setSavingFallback(false);
    }
  };

  // Agent pipeline handlers
  const handleAgentModelChange = (agentKey, field, value) => {
    setAgentModels(prev => ({
      ...prev,
      [agentKey]: {
        ...prev[agentKey],
        [field]: value,
        // Reset model to first available when provider changes
        ...(field === 'provider' ? { model: getProviderModels(value)?.[0]?.value || '' } : {})
      }
    }));
  };

  const handleSaveAgentConfig = async () => {
    setSavingAgentConfig(true);
    setMessage({ type: '', text: '' });
    try {
      await api.admin.updateAgentModels({
        useAgentPipeline: agentPipelineEnabled,
        qualityThreshold,
        maxRetries,
        minWordCount,
        agentModels
      });
      setMessage({ type: 'success', text: 'Agent pipeline configuration saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save agent pipeline configuration' });
    } finally {
      setSavingAgentConfig(false);
    }
  };

  // Build model dropdown options dynamically from backend Config.models
  // Falls back to hardcoded PROVIDER_MODELS if backend data not loaded.
  // Appends ZDR tag + input/output price per MTok where available.
  const getProviderModels = (provider) => {
    if (!backendModels) {
      const fallback = PROVIDER_MODELS[provider] || [];
      return [...fallback].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    }
    const entries = Object.entries(backendModels)
      .filter(([, cfg]) => cfg.provider === provider && cfg.enabled !== false)
      // OpenRouter: show ZDR-only models (compliance requirement).
      // All other providers: show all models, tag ZDR where applicable.
      .filter(([, cfg]) => provider !== 'openrouter' || cfg.zdr === true)
      .map(([id, cfg]) => {
        const baseName = cfg.name || id;
        const zdrTag = cfg.zdr === true ? ' [ZDR]' : '';
        const priceTag = (cfg.inputPrice != null && cfg.outputPrice != null)
          ? ` ($${Number(cfg.inputPrice).toFixed(2)}/$${Number(cfg.outputPrice).toFixed(2)})`
          : '';
        return { value: id, label: `${baseName}${zdrTag}${priceTag}` };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return entries.length > 0 ? entries : [...(PROVIDER_MODELS[provider] || [])].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  };

  // Lookup helper for displaying a model's badge data (ZDR + pricing) next to dropdowns.
  const getModelMeta = (modelId) => {
    if (!backendModels || !modelId) return null;
    return backendModels[modelId] || null;
  };

  const handleSaveClaudeConfig = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      // Fetch current AI settings, update Claude fields, save back
      const currentSettings = await api.admin.getAISettings();
      currentSettings.claudeEffortLevel = config.claudeEffortLevel;
      currentSettings.claudeMaxWebSearches = config.claudeMaxWebSearches;
      await api.admin.updateConfig({ key: 'ai_settings', value: currentSettings });
      setMessage({ type: 'success', text: 'Claude configuration saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save Claude configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestProvider = async (provider) => {
    setTestingProvider(provider);
    try {
      const result = await api.admin.testProvider(provider);
      setProviderTestResults(prev => ({ ...prev, [provider]: result }));
    } catch (err) {
      setProviderTestResults(prev => ({
        ...prev,
        [provider]: { connected: false, error: err.message }
      }));
    } finally {
      setTestingProvider(null);
    }
  };

  const handleTestAllProviders = async () => {
    const providers = ['openai', 'anthropic', 'google', 'xai', 'openrouter'];
    for (const provider of providers) {
      await handleTestProvider(provider);
    }
  };

  const handleRefreshModels = async () => {
    setRefreshingModels(true);
    setRefreshResult(null);
    setMessage({ type: '', text: '' });
    try {
      const result = await api.admin.refreshModels();
      setRefreshResult(result);
      setMessage({ type: 'success', text: result.message || `Refreshed: ${result.changeCount} change(s), ${result.totalModels} model(s) total.` });
      // Re-fetch AI settings so backendModels is updated with the new ZDR entries
      const aiSettingsResponse = await api.admin.getAISettings();
      if (aiSettingsResponse?.models) {
        setBackendModels(aiSettingsResponse.models);
      }
      // Refresh the cron history strip so this run shows up at the top
      loadUpdaterHistory();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to refresh model registry' });
    } finally {
      setRefreshingModels(false);
    }
  };

  // Pull the COMPLETE Bedrock model catalogue for the selected region live
  // from AWS (ListFoundationModels + ListInferenceProfiles) and merge into
  // the model registry. Save AWS credentials first.
  const handleRefreshBedrockModels = async () => {
    setRefreshingModels(true);
    setRefreshResult(null);
    setMessage({ type: '', text: '' });
    try {
      const result = await api.admin.refreshBedrockModels(config.bedrockRegion);
      setRefreshResult(result);
      setMessage({ type: 'success', text: result.message || `Bedrock ${result.region}: ${result.added} model(s) discovered.` });
      const aiSettingsResponse = await api.admin.getAISettings();
      if (aiSettingsResponse?.models) setBackendModels(aiSettingsResponse.models);
    } catch (err) {
      setMessage({ type: 'error', text: (err.message || 'Failed to refresh Bedrock models') + (err.hint ? ` — ${err.hint}` : '') });
    } finally {
      setRefreshingModels(false);
    }
  };

  const loadUpdaterHistory = async () => {
    try {
      const data = await api.admin.getModelUpdaterHistory();
      setUpdaterHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load updater history:', err.message);
    }
  };

  // Build list of ZDR models from backend registry
  const zdrModels = backendModels
    ? Object.entries(backendModels)
        .filter(([, cfg]) => cfg.zdr === true && cfg.enabled !== false)
        .map(([id, cfg]) => ({ id, ...cfg }))
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
    : [];

  // Dynamic model list for fallback chain dropdowns — live ZDR + pricing from DB
  // Falls back to the static ALL_MODELS list if backend data isn't loaded yet.
  const dynamicDropdownModels = backendModels
    ? Object.entries(backendModels)
        .filter(([, cfg]) => cfg.enabled !== false)
        .map(([id, cfg]) => {
          const zdrTag = cfg.zdr === true ? ' ✓ZDR' : cfg.zdr === false ? ' ✗non-ZDR' : '';
          const searchTag = cfg.hasNativeSearch ? ' 🔍' : '';
          const priceTag = cfg.inputPrice != null ? ` ($${cfg.inputPrice}/$${cfg.outputPrice})` : '';
          return { value: id, label: `${id}${zdrTag}${searchTag}${priceTag}` };
        })
        .sort((a, b) => a.value.localeCompare(b.value))
    : ALL_MODELS.map(m => ({ value: m.value, label: m.label }));

  const openRouterModels = backendModels
    ? Object.entries(backendModels)
        .filter(([, cfg]) => cfg.provider === 'openrouter' && cfg.enabled !== false)
        .map(([id, cfg]) => ({ id, ...cfg }))
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Due Diligence Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              User Portal
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <Link to="/admin-portal-7x9k2m/dashboard" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Dashboard
            </Link>
            <Link to="/admin-portal-7x9k2m/reports" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Reports
            </Link>
            <Link to="/admin-portal-7x9k2m/users" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Users
            </Link>
            <Link to="/admin-portal-7x9k2m/config" className="px-4 py-3 text-sm font-medium text-white border-b-2 border-red-500">
              Configuration
            </Link>
            <Link to="/admin-portal-7x9k2m/vouchers" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Vouchers
            </Link>
            <Link to="/admin-portal-7x9k2m/reviews" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Reviews
            </Link>
            <Link to="/admin-portal-7x9k2m/audit" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              Audit
            </Link>
            <Link to="/admin-portal-7x9k2m/api-keys" className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600">
              API Keys
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-200' :
            'bg-red-900/50 border border-red-700 text-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-6">
          {/* Feature Flags (Admin Preview) */}
          <div className="bg-gray-800 rounded-lg border border-amber-700/40 p-6">
            <button
              type="button"
              onClick={() => setShowFeatureFlags(!showFeatureFlags)}
              className="w-full flex items-center justify-between"
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span className="text-amber-400">⚑</span>
                  Feature Flags — Admin Preview
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Toggles for in-progress modules. All flags default to OFF and have no user-facing effect unless explicitly enabled. Use these to test new features before public rollout.
                </p>
              </div>
              <span className="text-gray-400 text-sm flex-shrink-0 ml-4">{showFeatureFlags ? '▲ Hide' : '▼ Show'}</span>
            </button>

            {showFeatureFlags && (
              <div className="mt-6 space-y-2">
                {Object.keys(features).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No feature flags loaded.</p>
                ) : (
                  Object.entries(FEATURE_FLAG_DESCRIPTIONS).map(([flagName, meta]) => {
                    if (!(flagName in features)) return null;
                    const enabled = features[flagName] === true;
                    const isSaving = savingFeature === flagName;
                    return (
                      <div
                        key={flagName}
                        className="flex items-start justify-between gap-4 p-3 bg-gray-900/40 border border-gray-700 rounded-md"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <code className="text-xs text-amber-300 font-mono">{flagName}</code>
                            {meta.risk && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide ${
                                meta.risk === 'high' ? 'bg-red-900/60 text-red-200' :
                                meta.risk === 'medium' ? 'bg-amber-900/60 text-amber-200' :
                                'bg-gray-700 text-gray-300'
                              }`}>{meta.risk}</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-300 mt-1">{meta.label}</p>
                          {meta.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{meta.description}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          disabled={isSaving}
                          onClick={() => handleToggleFeature(flagName)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 mt-1 ${
                            enabled ? 'bg-red-600' : 'bg-gray-600'
                          } ${isSaving ? 'opacity-50 cursor-wait' : ''}`}
                          title={enabled ? 'Click to disable' : 'Click to enable'}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    );
                  })
                )}

                {/* Show any flags that exist server-side but aren't in our description map */}
                {Object.keys(features).filter(k => !(k in FEATURE_FLAG_DESCRIPTIONS)).map(flagName => {
                  const enabled = features[flagName] === true;
                  const isSaving = savingFeature === flagName;
                  return (
                    <div
                      key={flagName}
                      className="flex items-start justify-between gap-4 p-3 bg-gray-900/40 border border-gray-700 rounded-md"
                    >
                      <div className="flex-1 min-w-0">
                        <code className="text-xs text-amber-300 font-mono">{flagName}</code>
                        <p className="text-xs text-gray-500 mt-1">No description available.</p>
                      </div>
                      <button
                        type="button"
                        disabled={isSaving}
                        onClick={() => handleToggleFeature(flagName)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 mt-1 ${
                          enabled ? 'bg-red-600' : 'bg-gray-600'
                        } ${isSaving ? 'opacity-50 cursor-wait' : ''}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  );
                })}

                <p className="text-xs text-gray-500 mt-3 italic">
                  Note: changes take effect within 30 seconds (cache TTL). Flags persist in <code className="text-gray-400">ai_settings.features</code>.
                </p>
              </div>
            )}
          </div>

          {/* Agent Pipeline Configuration */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">Agent Pipeline Configuration</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Multi-agent pipeline uses specialised AI models for each stage of report generation.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAgentPipelineEnabled(!agentPipelineEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                  agentPipelineEnabled ? 'bg-red-600' : 'bg-gray-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  agentPipelineEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {agentPipelineEnabled && (
              <div className="space-y-6">
                {/* Pipeline Flow Diagram */}
                <div className="p-4 bg-gray-900/50 border border-gray-700 rounded-md">
                  <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Pipeline Flow</p>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="px-2 py-1 bg-blue-900/50 border border-blue-700 rounded text-blue-200">1. Web Research</span>
                    <span className="text-gray-500">&rarr;</span>
                    <div className="flex flex-col gap-1">
                      <span className="px-2 py-1 bg-purple-900/50 border border-purple-700 rounded text-purple-200">2a. Deep Research</span>
                      <span className="px-2 py-1 bg-indigo-900/50 border border-indigo-700 rounded text-indigo-200">2b. Advanced Research</span>
                    </div>
                    <span className="text-gray-500">&rarr;</span>
                    <span className="px-2 py-1 bg-green-900/50 border border-green-700 rounded text-green-200">3. Arranger</span>
                    <span className="text-gray-500">&rarr;</span>
                    <span className="px-2 py-1 bg-yellow-900/50 border border-yellow-700 rounded text-yellow-200">4. Checker</span>
                    <span className="text-gray-500">&rarr;</span>
                    <span className="px-2 py-1 bg-red-900/50 border border-red-700 rounded text-red-200">5. Orchestrator</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Steps 2a and 2b run in parallel. If quality score is below threshold, one retry cycle runs targeted research + re-arrange + re-check.
                  </p>
                </div>

                {/* How the pipeline works */}
                <div className="bg-blue-900/20 border border-blue-800 rounded-md p-4">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-300">
                      <strong>How the pipeline works:</strong>
                      <ol className="list-decimal ml-4 mt-2 space-y-1 text-xs text-blue-200">
                        <li><strong>Web Research Agent</strong> — broad web searching across all topic areas</li>
                        <li><strong>Deep Research Agent</strong> — deep OSINT investigation (runs in parallel with Advanced Research; receives web research to avoid duplication)</li>
                        <li><strong>Advanced Research Agent</strong> — in-depth analytical research: financial patterns, corporate structure, legal/regulatory, reputation (runs in parallel with Deep Research)</li>
                        <li>All 3 research outputs are <strong>merged (Part A + B + C)</strong> and fed to the Arranger</li>
                        <li><strong>Arranger Agent</strong> — writes the full formatted due diligence report from combined research</li>
                        <li><strong>Checker Agent</strong> — quality assessment and scoring; if below threshold, one retry cycle runs</li>
                        <li><strong>Orchestrator Agent</strong> — final review for coherence, tone, and professional polish</li>
                      </ol>
                      <p className="mt-2 text-xs text-blue-300/70">
                        If any agent's configured model fails, the fallback chain (configured below) is tried automatically.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quality Threshold */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quality Threshold (score below triggers retry)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="0.5"
                      value={qualityThreshold}
                      onChange={(e) => setQualityThreshold(parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                    />
                    <span className="text-white font-mono text-lg w-10 text-center">{qualityThreshold}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Reports scoring below this threshold will trigger retry cycles with targeted research. Recommended: 7
                  </p>
                </div>

                {/* Max Retries */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Maximum Retry Attempts (when quality score is below threshold)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="5"
                      step="1"
                      value={maxRetries}
                      onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                    />
                    <span className="text-white font-mono text-lg w-10 text-center">{maxRetries}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Number of research-rearrange-check cycles before accepting. Each retry adds ~10-15 minutes. Recommended: 2
                  </p>
                </div>

                {/* Minimum Word Count */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Minimum Word Count (retry if report is too short)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="4000"
                      max="15000"
                      step="500"
                      value={minWordCount}
                      onChange={(e) => setMinWordCount(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                    />
                    <span className="text-white font-mono text-lg w-16 text-center">{minWordCount.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Reports below this word count will trigger a retry even if the quality score passes. Target ~10,000 words. Recommended: 8,000
                  </p>
                </div>

                {/* Agent Model Configuration */}
                <div className="space-y-4">
                  <h3 className="text-md font-medium text-white">Agent Model Assignments</h3>

                  {AGENT_ROLES.map((agent) => (
                    <div key={agent.key} className="p-4 bg-gray-900/30 border border-gray-700 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                              {agent.step}
                            </span>
                            <h4 className="text-sm font-semibold text-white">{agent.label}</h4>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 ml-7">{agent.description}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-7">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Provider</label>
                          <select
                            value={agentModels[agent.key]?.provider || 'anthropic'}
                            onChange={(e) => handleAgentModelChange(agent.key, 'provider', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            {PROVIDER_OPTIONS.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Model</label>
                          <select
                            value={agentModels[agent.key]?.model || ''}
                            onChange={(e) => handleAgentModelChange(agent.key, 'model', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            {(() => {
                              const providerModels = getProviderModels(agentModels[agent.key]?.provider);
                              const currentModel = agentModels[agent.key]?.model;
                              const modelInList = providerModels.some(m => m.value === currentModel);
                              return (
                                <>
                                  {!modelInList && currentModel && (
                                    <option value={currentModel}>{currentModel} (stored)</option>
                                  )}
                                  {providerModels.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                  ))}
                                </>
                              );
                            })()}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Max Output Tokens</label>
                          <input
                            type="number"
                            min="1000"
                            max={API_MAX_TOKENS[agentModels[agent.key]?.model] || 128000}
                            step="1000"
                            value={agentModels[agent.key]?.maxTokens || ''}
                            onChange={(e) => handleAgentModelChange(agent.key, 'maxTokens', parseInt(e.target.value) || 0)}
                            placeholder={`Default: ${(API_MAX_TOKENS[agentModels[agent.key]?.model] || 'max').toLocaleString()}`}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            API max: {(API_MAX_TOKENS[agentModels[agent.key]?.model] || 0).toLocaleString() || '—'}. Empty = use max.
                          </p>
                        </div>
                      </div>
                      {/* Web Search Toggle */}
                      <div className="mt-3 ml-7 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => handleAgentModelChange(agent.key, 'webSearch', !(agentModels[agent.key]?.webSearch ?? true))}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                              (agentModels[agent.key]?.webSearch ?? true)
                                ? 'bg-blue-600'
                                : 'bg-gray-600'
                            }`}
                            title="Toggle web search for this agent"
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                (agentModels[agent.key]?.webSearch ?? true) ? 'translate-x-4' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                          <span className="text-xs text-gray-400">
                            Web Search{' '}
                            <span className={`font-semibold ${(agentModels[agent.key]?.webSearch ?? true) ? 'text-blue-400' : 'text-gray-500'}`}>
                              {(agentModels[agent.key]?.webSearch ?? true) ? 'ON' : 'OFF'}
                            </span>
                          </span>
                          {agentModels[agent.key]?.provider === 'openrouter' && (agentModels[agent.key]?.webSearch ?? true) && (
                            <span className="text-[10px] text-gray-500 italic">
                              {['perplexity/sonar', 'perplexity/sonar-deep-research', 'perplexity/sonar-pro'].includes(agentModels[agent.key]?.model)
                                ? '(native search — no plugin added)'
                                : `(OR web plugin: id="web", max_results=${agentModels[agent.key]?.maxSearches || 50})`}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Max Searches — only relevant for OpenRouter web-plugin agents */}
                      {agentModels[agent.key]?.provider === 'openrouter' &&
                       (agentModels[agent.key]?.webSearch ?? true) &&
                       !['perplexity/sonar', 'perplexity/sonar-deep-research', 'perplexity/sonar-pro'].includes(agentModels[agent.key]?.model) && (
                        <div className="mt-2 ml-7 flex items-center gap-3">
                          <label className="text-xs text-gray-400 whitespace-nowrap">Max Searches</label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={agentModels[agent.key]?.maxSearches ?? 50}
                            onChange={e => handleAgentModelChange(agent.key, 'maxSearches', Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 50)))}
                            className="w-20 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            title="Maximum web searches for this agent (1–100)"
                          />
                          <span className="text-[10px] text-gray-500">searches (1–100, default 50)</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Info Box */}
                <div className="p-4 bg-gray-900/50 border border-gray-700 rounded-md">
                  <div className="flex items-start gap-3">
                    <Settings className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-400">
                      <p className="font-medium text-white mb-1">How it works:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li><strong className="text-white">Web Research:</strong> Broad searches across the web for company info, news, sanctions lists</li>
                        <li><strong className="text-white">Deep Research:</strong> OSINT investigation - company registries, courts, patents, academic sources</li>
                        <li><strong className="text-white">Arranger:</strong> Combines all research into a professionally formatted report</li>
                        <li><strong className="text-white">Checker:</strong> Scores the report for quality, completeness and accuracy</li>
                        <li><strong className="text-white">Orchestrator:</strong> Final review pass for coherence and professional tone</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Save Agent Config Button */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                  <p className="text-xs text-gray-500">
                    Changes only take effect for new reports. In-progress reports use their original configuration.
                  </p>
                  <button
                    type="button"
                    onClick={handleSaveAgentConfig}
                    disabled={savingAgentConfig}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingAgentConfig ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Agent Configuration
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {!agentPipelineEnabled && (
              <div className="p-4 bg-yellow-900/20 border border-yellow-700 rounded-md">
                <p className="text-sm text-yellow-200">
                  Multi-agent pipeline is disabled. Reports will use the legacy two-call pipeline
                  (single model for research and writing).
                </p>
              </div>
            )}
          </div>

          {/* AI Provider Settings */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">AI Provider Settings</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Active Provider</label>
                <select
                  value={config.aiProvider}
                  onChange={(e) => setConfig({ ...config, aiProvider: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {PROVIDER_OPTIONS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              {/* OpenAI */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-md font-medium text-white mb-3">OpenAI</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys.openai ? 'text' : 'password'}
                        value={config.openaiApiKey}
                        onChange={(e) => setConfig({ ...config, openaiApiKey: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="sk-..."
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('openai')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showKeys.openai ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Model</label>
                    <select
                      value={config.openaiModel}
                      onChange={(e) => setConfig({ ...config, openaiModel: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {getProviderModels('openai').map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Anthropic */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-md font-medium text-white mb-3">Anthropic</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys.anthropic ? 'text' : 'password'}
                        value={config.anthropicApiKey}
                        onChange={(e) => setConfig({ ...config, anthropicApiKey: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="sk-ant-..."
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('anthropic')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showKeys.anthropic ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Model</label>
                    <select
                      value={config.anthropicModel}
                      onChange={(e) => setConfig({ ...config, anthropicModel: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {getProviderModels('anthropic').map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Google */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-md font-medium text-white mb-3">Google</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys.google ? 'text' : 'password'}
                        value={config.googleApiKey}
                        onChange={(e) => setConfig({ ...config, googleApiKey: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="AI..."
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('google')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showKeys.google ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Model</label>
                    <select
                      value={config.googleModel}
                      onChange={(e) => setConfig({ ...config, googleModel: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {getProviderModels('google').map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* xAI */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-md font-medium text-white mb-3">xAI (Grok)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys.xai ? 'text' : 'password'}
                        value={config.xaiApiKey}
                        onChange={(e) => setConfig({ ...config, xaiApiKey: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="xai-..."
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('xai')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showKeys.xai ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Model</label>
                    <select
                      value={config.xaiModel || getProviderModels('xai')[0]?.value}
                      onChange={(e) => setConfig({ ...config, xaiModel: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {getProviderModels('xai').map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* OpenRouter */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-md font-medium text-white mb-3">OpenRouter</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">API Key</label>
                    <div className="relative">
                      <input
                        type={showKeys.openrouter ? 'text' : 'password'}
                        value={config.openrouterApiKey}
                        onChange={(e) => setConfig({ ...config, openrouterApiKey: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="sk-or-..."
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowKey('openrouter')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showKeys.openrouter ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Model</label>
                    <select
                      value={config.openrouterModel || getProviderModels('openrouter')[0]?.value}
                      onChange={(e) => setConfig({ ...config, openrouterModel: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {getProviderModels('openrouter').map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Amazon Bedrock */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-md font-medium text-white mb-1">Amazon Bedrock</h3>
                <p className="text-xs text-gray-500 mb-3">
                  AWS IAM credentials (needs <code className="text-gray-400">bedrock:Converse</code>,
                  <code className="text-gray-400"> bedrock:ListFoundationModels</code>,
                  <code className="text-gray-400"> bedrock:ListInferenceProfiles</code>).
                  Region defaults to eu-west-2 (London) for UK/EU data residency.
                  The model list is pulled live from the selected region — click
                  “Refresh Bedrock models” after saving credentials.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">AWS Access Key ID</label>
                    <div className="relative">
                      <input
                        type={showKeys.bedrockAk ? 'text' : 'password'}
                        value={config.bedrockAccessKeyId}
                        onChange={(e) => setConfig({ ...config, bedrockAccessKeyId: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="AKIA..."
                      />
                      <button type="button" onClick={() => toggleShowKey('bedrockAk')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white">
                        {showKeys.bedrockAk ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">AWS Secret Access Key</label>
                    <div className="relative">
                      <input
                        type={showKeys.bedrockSk ? 'text' : 'password'}
                        value={config.bedrockSecretAccessKey}
                        onChange={(e) => setConfig({ ...config, bedrockSecretAccessKey: e.target.value })}
                        className="w-full px-4 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="••••••••••••••••••••"
                      />
                      <button type="button" onClick={() => toggleShowKey('bedrockSk')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white">
                        {showKeys.bedrockSk ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Region</label>
                    <select
                      value={config.bedrockRegion}
                      onChange={(e) => setConfig({ ...config, bedrockRegion: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {BEDROCK_REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Model</label>
                    <select
                      value={config.bedrockModel || getProviderModels('bedrock')[0]?.value || ''}
                      onChange={(e) => setConfig({ ...config, bedrockModel: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {getProviderModels('bedrock').length === 0
                        ? <option value="">(none — save creds + Refresh Bedrock models)</option>
                        : getProviderModels('bedrock').map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleRefreshBedrockModels}
                    disabled={refreshingModels}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white text-sm rounded-md inline-flex items-center gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${refreshingModels ? 'animate-spin' : ''}`} />
                    {refreshingModels ? 'Discovering…' : `Refresh Bedrock models (${config.bedrockRegion})`}
                  </button>
                  <span className="text-xs text-gray-500 ml-3">
                    Pulls the complete live catalogue for the selected region.
                  </span>
                </div>
              </div>

              {/* Update API Keys Button */}
              <div className="border-t border-gray-700 pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveApiKeys}
                  disabled={savingApiKeys}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingApiKeys ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving Keys...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Update API Keys
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* API Provider Status */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">API Provider Status</h2>
                <p className="text-sm text-gray-400 mt-1">Connection status for configured AI providers</p>
              </div>
              <button
                type="button"
                onClick={handleTestAllProviders}
                disabled={testingProvider !== null}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                <RefreshCw className={`h-4 w-4 ${testingProvider ? 'animate-spin' : ''}`} />
                Test All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'openai', name: 'OpenAI', description: 'GPT-5.4, GPT-5, O3 Pro, O4 series', subProviders: [] },
                { key: 'anthropic', name: 'Anthropic', description: 'Claude Opus 4.6, Sonnet 4.6', subProviders: [] },
                { key: 'google', name: 'Google', description: 'Gemini 3.1 Pro, Flash Lite', subProviders: [] },
                { key: 'xai', name: 'xAI', description: 'Grok 4, Grok 4.1 Fast', subProviders: [] },
                { key: 'openrouter', name: 'OpenRouter', description: 'MoonshotAI, Grok, & more', subProviders: ['MoonshotAI (Kimi)'] },
              ].map(provider => {
                const hasKey = apiKeyStatus[provider.key];
                const testResult = providerTestResults[provider.key];
                const isTesting = testingProvider === provider.key;

                let statusColor = 'bg-gray-600'; // unknown
                let statusText = 'Not configured';
                let StatusIcon = WifiOff;

                if (hasKey && testResult?.connected) {
                  statusColor = 'bg-green-600';
                  statusText = 'Connected';
                  StatusIcon = Wifi;
                } else if (hasKey && testResult && !testResult.connected) {
                  statusColor = 'bg-red-600';
                  statusText = 'Error';
                  StatusIcon = WifiOff;
                } else if (hasKey) {
                  statusColor = 'bg-yellow-600';
                  statusText = 'Key set — not tested';
                  StatusIcon = Wifi;
                }

                return (
                  <div key={provider.key} className="p-4 bg-gray-900/30 border border-gray-700 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${statusColor} flex-shrink-0`} />
                        <div>
                          <h4 className="text-sm font-semibold text-white">{provider.name}</h4>
                          <p className="text-xs text-gray-400">{provider.description}</p>
                          {provider.subProviders.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              Routes: {provider.subProviders.join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleTestProvider(provider.key)}
                        disabled={isTesting || !hasKey}
                        className="flex items-center gap-1 px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {isTesting ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        Test
                      </button>
                    </div>

                    <div className="mt-2 ml-6">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        hasKey && testResult?.connected ? 'bg-green-900/50 text-green-300 border border-green-700' :
                        hasKey && testResult && !testResult.connected ? 'bg-red-900/50 text-red-300 border border-red-700' :
                        hasKey ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-700' :
                        'bg-gray-800 text-gray-400 border border-gray-600'
                      }`}>
                        {statusText}
                      </span>
                      {testResult?.modelInfo && (
                        <span className="text-xs text-gray-400 ml-2">{testResult.modelInfo}</span>
                      )}
                      {testResult?.error && (
                        <p className="text-xs text-red-400 mt-1">{testResult.error}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 p-3 bg-gray-900/50 border border-gray-700 rounded-md">
              <p className="text-xs text-gray-400">
                <strong className="text-white">Note:</strong> API keys are configured via environment variables on the server.
                Contact your system administrator to add or change API keys. MoonshotAI and Grok are accessed via OpenRouter.
              </p>
            </div>
          </div>

          {/* OpenRouter Model Registry */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">OpenRouter Model Registry</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Auto-discovered OpenRouter models, ZDR (Zero Data Retention) flags and ex-VAT pricing.
                  The daily cron (03:00 UTC) refreshes this list and auto-replaces any non-ZDR model
                  assigned to a research agent. Use the button to pull an immediate update.
                </p>
              </div>
              <button
                type="button"
                onClick={handleRefreshModels}
                disabled={refreshingModels}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex-shrink-0"
              >
                {refreshingModels ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Refresh OpenRouter Models
                  </>
                )}
              </button>
            </div>

            {/* Refresh result summary */}
            {refreshResult && (
              <div className="mb-4 p-3 bg-gray-900/50 border border-gray-700 rounded-md text-xs text-gray-300">
                <strong className="text-white">Last refresh:</strong>{' '}
                {refreshResult.changeCount} change{refreshResult.changeCount === 1 ? '' : 's'} applied.{' '}
                <strong className="text-white">{refreshResult.totalModels}</strong> model{refreshResult.totalModels === 1 ? '' : 's'} in registry.
                {refreshResult.zdrReplacements && refreshResult.zdrReplacements.length > 0 && (
                  <div className="mt-2 text-amber-200">
                    <strong>{refreshResult.zdrReplacements.length} ZDR replacement(s)</strong> applied to research agents.
                  </div>
                )}
              </div>
            )}

            {/* Cron history strip */}
            {updaterHistory.length > 0 && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setShowUpdaterHistory(!showUpdaterHistory)}
                  className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                >
                  {showUpdaterHistory ? '▼' : '▶'} Cron run history (last {updaterHistory.length})
                  <span className="ml-2 text-gray-500">
                    — most recent: {new Date(updaterHistory[0].ranAt).toLocaleString()}
                    {' · '}{updaterHistory[0].triggeredBy}
                  </span>
                </button>
                {showUpdaterHistory && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {updaterHistory.map(h => {
                      const repls = h.details?.zdrReplacements || h.details?.replacements || [];
                      return (
                        <div key={h._id} className="text-xs bg-gray-900/40 border border-gray-700 rounded px-3 py-1.5 text-gray-300">
                          <span className="text-gray-500">{new Date(h.ranAt).toLocaleString()}</span>
                          {' · '}
                          <span className={h.triggeredBy === 'cron' ? 'text-blue-300' : 'text-amber-300'}>
                            {h.triggeredBy}
                          </span>
                          {' · '}
                          <span className="text-gray-400">{h.action.replace(/_/g, ' ')}</span>
                          {h.details?.changeCount != null && (
                            <span className="ml-2 text-gray-500">
                              ({h.details.changeCount} change{h.details.changeCount === 1 ? '' : 's'})
                            </span>
                          )}
                          {repls.length > 0 && (
                            <span className="ml-2 text-amber-300">
                              · {repls.length} ZDR replacement{repls.length === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Summary counts */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-gray-900/30 border border-gray-700 rounded-md">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Total OpenRouter models</p>
                <p className="text-2xl font-mono text-white mt-1">{openRouterModels.length}</p>
              </div>
              <div className="p-3 bg-purple-900/20 border border-purple-800 rounded-md">
                <p className="text-xs text-purple-300 uppercase tracking-wide">ZDR models</p>
                <p className="text-2xl font-mono text-purple-200 mt-1">{zdrModels.length}</p>
              </div>
              <div className="p-3 bg-gray-900/30 border border-gray-700 rounded-md">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Non-ZDR (curated)</p>
                <p className="text-2xl font-mono text-white mt-1">{openRouterModels.length - zdrModels.length}</p>
              </div>
            </div>

            {/* ZDR list toggle */}
            <button
              type="button"
              onClick={() => setShowZdrList(!showZdrList)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors text-sm"
            >
              {showZdrList ? 'Hide' : 'Show'} Full ZDR Model List ({zdrModels.length})
            </button>

            {showZdrList && zdrModels.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="py-2 pr-3 font-medium">Model ID</th>
                      <th className="py-2 pr-3 font-medium">Display Name</th>
                      <th className="py-2 pr-3 font-medium text-right">Input $/MTok</th>
                      <th className="py-2 pr-3 font-medium text-right">Output $/MTok</th>
                      <th className="py-2 pr-3 font-medium text-right">Web $/1k</th>
                      <th className="py-2 pr-3 font-medium text-right">Context</th>
                      <th className="py-2 pr-3 font-medium text-right">Max Out</th>
                      <th className="py-2 font-medium text-center">Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zdrModels.map(m => (
                      <tr key={m.id} className="border-b border-gray-800 text-gray-300">
                        <td className="py-2 pr-3 font-mono text-[10px] text-gray-400">{m.id}</td>
                        <td className="py-2 pr-3">{m.name || m.id}</td>
                        <td className="py-2 pr-3 font-mono text-right">
                          {m.inputPrice != null ? `$${Number(m.inputPrice).toFixed(2)}` : '—'}
                        </td>
                        <td className="py-2 pr-3 font-mono text-right">
                          {m.outputPrice != null ? `$${Number(m.outputPrice).toFixed(2)}` : '—'}
                        </td>
                        <td className="py-2 pr-3 font-mono text-right">
                          {m.webSearchPrice != null ? `$${Number(m.webSearchPrice * 1000).toFixed(2)}` : '—'}
                        </td>
                        <td className="py-2 pr-3 font-mono text-right">
                          {m.contextWindow ? `${(m.contextWindow / 1000).toFixed(0)}K` : '—'}
                        </td>
                        <td className="py-2 pr-3 font-mono text-right">
                          {m.maxOutput ? `${(m.maxOutput / 1000).toFixed(0)}K` : '—'}
                        </td>
                        <td className="py-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <span className="inline-block px-1.5 py-0.5 bg-purple-900/40 border border-purple-700 text-purple-200 rounded text-[10px] font-semibold">
                              ZDR
                            </span>
                            {m.hasNativeSearch && (
                              <span className="inline-block px-1.5 py-0.5 bg-emerald-900/40 border border-emerald-700 text-emerald-200 rounded text-[10px] font-semibold" title="Native web search supported">
                                SEARCH
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {showZdrList && zdrModels.length === 0 && (
              <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-700 rounded-md">
                <p className="text-sm text-yellow-200">
                  No ZDR models in registry yet. Click <strong>Refresh OpenRouter Models</strong> above to pull the latest ZDR list from OpenRouter.
                </p>
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-800 rounded-md">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-200">
                  <strong>ZDR</strong> = Zero Data Retention. OpenRouter fetches the authoritative ZDR list from{' '}
                  <code className="bg-blue-950/60 px-1 rounded text-[10px]">/api/v1/models?supported_parameters=zdr</code>.
                  ZDR models do not retain prompts or completions at the upstream provider — these are the privacy-safe pool for sensitive DD work.
                  Pricing is ex-VAT and sourced directly from OpenRouter per-token rates.
                </p>
              </div>
            </div>
          </div>

          {/* Fallback Model Chain */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Fallback Model Chain</h2>
            <p className="text-sm text-gray-400 mb-4">
              When an agent's configured model or provider fails, the pipeline will try these fallback models in order.
              Configure up to 4 fallback models across any provider.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Primary Fallback (1st)</label>
                <select
                  value={systemDefaultModel}
                  onChange={(e) => setSystemDefaultModel(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {dynamicDropdownModels.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {[0, 1, 2].map(idx => (
                <div key={idx}>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {idx === 0 ? '2nd' : idx === 1 ? '3rd' : '4th'} Fallback
                  </label>
                  <select
                    value={fallbackModels[idx] || ''}
                    onChange={(e) => {
                      const updated = [...fallbackModels];
                      updated[idx] = e.target.value;
                      setFallbackModels(updated);
                    }}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">(None — stop here)</option>
                    {dynamicDropdownModels.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              ))}

              <button
                type="button"
                onClick={handleSaveFallbackChain}
                disabled={savingFallback}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {savingFallback ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Fallback Chain
                  </>
                )}
              </button>

              <div className="bg-gray-900/50 border border-gray-700 rounded-md p-4">
                <p className="text-sm text-gray-400">
                  <strong className="text-white">Note:</strong> The fallback chain is used when an agent's primary model fails (e.g. API error, rate limit, timeout).
                  Each agent in the pipeline has its own primary model configured below. The fallback chain is shared across all agents.
                </p>
              </div>
            </div>
          </div>

          {/* Claude Advanced Configuration */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Claude Advanced Configuration
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              Configure Claude-specific parameters for extended thinking and web research.
              These settings apply to all Claude models (Opus, Sonnet) when generating reports.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Effort Level */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Extended Thinking Effort Level
                </label>
                <select
                  value={config.claudeEffortLevel || 'high'}
                  onChange={(e) => setConfig({ ...config, claudeEffortLevel: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="low">Low - Faster, basic analysis</option>
                  <option value="medium">Medium - Balanced performance</option>
                  <option value="high">High - Thorough research (Recommended)</option>
                  <option value="max">Max - Deep analysis, slower</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Controls depth of extended thinking for Claude Opus 4.6
                </p>
              </div>

              {/* Max Web Searches */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Maximum Web Searches per Report
                </label>
                <input
                  type="number"
                  min="5"
                  max="100"
                  step="5"
                  value={config.claudeMaxWebSearches || 25}
                  onChange={(e) => setConfig({ ...config, claudeMaxWebSearches: parseInt(e.target.value) || 25 })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Spec minimum: 25 | Recommended: 25-50 | Max: 100
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Cost: $10 per 1,000 searches
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-4 p-4 bg-gray-900/50 border border-gray-700 rounded-md">
              <div className="flex items-start gap-3">
                <Settings className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-400">
                  <p className="font-medium text-white mb-1">Configuration Info:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong className="text-white">Effort Level:</strong> Controls Claude's thinking depth.
                      Higher levels = more thorough analysis but slower generation.</li>
                    <li><strong className="text-white">Max Searches:</strong> Limits web searches per report.
                      More searches = better fact-checking but higher costs ($0.01 per search).</li>
                    <li><strong className="text-white">Applies to:</strong> Claude Opus 4.6, Opus 4.5, Sonnet 4.5</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Cost Estimate */}
            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700 rounded-md">
              <p className="text-sm text-blue-200">
                <strong>Cost Estimate:</strong> With {config.claudeMaxWebSearches || 25} searches per report:
                <span className="font-mono ml-2">${((config.claudeMaxWebSearches || 25) * 0.01).toFixed(2)}</span> search cost + token costs
              </p>
            </div>

            <button
              type="button"
              onClick={handleSaveClaudeConfig}
              disabled={saving}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Claude Configuration
                </>
              )}
            </button>
          </div>

          {/* Pricing Settings */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Pricing Settings</h2>
              {config.stripeMode && (
                <span
                  className={`text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full border ${
                    config.stripeMode === 'live'
                      ? 'bg-green-900/40 border-green-600 text-green-300'
                      : config.stripeMode === 'test'
                        ? 'bg-amber-900/40 border-amber-600 text-amber-300'
                        : 'bg-gray-800 border-gray-600 text-gray-400'
                  }`}
                  title={
                    config.stripeMode === 'live'
                      ? 'Stripe is in LIVE mode — real card payments will be processed.'
                      : config.stripeMode === 'test'
                        ? 'Stripe is in TEST mode — no real payments will be taken. Use Stripe test cards.'
                        : 'STRIPE_SECRET_KEY not set on the backend.'
                  }
                >
                  Stripe: {config.stripeMode === 'live' ? 'LIVE' : config.stripeMode === 'test' ? 'TEST' : 'NOT CONFIGURED'}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 mb-4">All prices are in pence (e.g. 9900 = £99.00)</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Single Report Price</label>
                <input
                  type="number"
                  value={config.singleReportPrice}
                  onChange={(e) => setConfig({ ...config, singleReportPrice: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">= £{(config.singleReportPrice / 100).toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Essential Monthly</label>
                <input
                  type="number"
                  value={config.essentialMonthly}
                  onChange={(e) => setConfig({ ...config, essentialMonthly: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">= £{(config.essentialMonthly / 100).toFixed(2)}/month</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Professional Monthly</label>
                <input
                  type="number"
                  value={config.professionalMonthly}
                  onChange={(e) => setConfig({ ...config, professionalMonthly: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">= £{(config.professionalMonthly / 100).toFixed(2)}/month</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white font-medium rounded-md transition-colors flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminConfig;
